import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { 
  Search, Loader2, ArrowUpRight, ArrowDownLeft, Eye, Unlink,
  ChevronLeft, ChevronRight, FileText, CheckCircle2, Calendar
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function History() {
  const { token } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;
  
  // PDF Preview
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  
  // Unlink
  const [unlinking, setUnlinking] = useState(false);

  const fetchMatched = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/bridge-v2/rapprochement/matched`, {
        params: {
          skip: (page - 1) * pageSize,
          limit: pageSize
        },
        headers
      });
      setTransactions(res.data.transactions || []);
      setTotalCount(res.data.total || 0);
      setTotalPages(Math.ceil((res.data.total || 0) / pageSize));
    } catch (err) {
      console.error("Erreur chargement historique:", err);
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => {
    fetchMatched();
  }, [fetchMatched]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'Europe/Paris'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Paris'
    });
  };

  const handleViewPdf = (tx) => {
    if (!tx.facture) return;
    
    const isVente = tx.facture_rapprochee_type === 'vente';
    const endpoint = isVente 
      ? `${API}/api/pdfs/${tx.facture.numero}?token=${token}`
      : `${API}/api/supplier-invoices/${tx.facture.numero}/pdf?token=${token}`;
    
    setPdfUrl(endpoint);
    setSelectedTx(tx);
    setShowPdfModal(true);
  };

  const handleUnlink = async (tx) => {
    if (!token || unlinking) return;
    
    if (!window.confirm(`Êtes-vous sûr de vouloir annuler le rapprochement de cette transaction avec la facture ${tx.facture_rapprochee_numero} ?`)) {
      return;
    }
    
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setUnlinking(true);
      await axios.post(`${API}/api/bridge-v2/rapprochement/unlink/${tx.id}`, {}, { headers });
      await fetchMatched();
    } catch (err) {
      console.error("Erreur unlink:", err);
      alert("Erreur lors de l'annulation du rapprochement");
    } finally {
      setUnlinking(false);
    }
  };

  const getModeLabel = (tx) => {
    const statut = tx.statut_rapprochement;
    if (statut === 'rapproche_auto') return { label: 'Automatique', color: 'bg-green-100 text-green-700' };
    if (statut === 'rapproche_manuel') return { label: 'Manuel', color: 'bg-blue-100 text-blue-700' };
    if (statut === 'rapproche_remise_sepa') return { label: 'Remise SEPA', color: 'bg-purple-100 text-purple-700' };
    return { label: 'Rapproché', color: 'bg-slate-100 text-slate-700' };
  };

  // Filtrer les transactions selon la recherche
  const filteredTransactions = transactions.filter(tx => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      (tx.clean_description || '').toLowerCase().includes(searchLower) ||
      (tx.facture_rapprochee_numero || '').toLowerCase().includes(searchLower) ||
      (tx.facture?.client || '').toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6" data-testid="history-page">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Historique des rapprochements</h1>
          <p className="text-slate-500 mt-1">{totalCount} transactions rapprochées</p>
        </div>
      </div>

      {/* Recherche */}
      <Card>
        <CardContent className="pt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher par libellé, facture, client..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Liste */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Aucun rapprochement trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Transaction</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase">Montant</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Facture liée</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-slate-500 uppercase">Mode</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredTransactions.map((tx) => {
                    const isCredit = tx.amount > 0;
                    const mode = getModeLabel(tx);
                    
                    return (
                      <tr 
                        key={tx.id} 
                        className="hover:bg-slate-50 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="text-sm text-slate-900">{formatDate(tx.date)}</div>
                          <div className="text-xs text-slate-500">
                            Rapproché le {formatDateTime(tx.date_rapprochement)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className={`p-1 rounded ${isCredit ? 'bg-green-100' : 'bg-red-100'}`}>
                              {isCredit ? (
                                <ArrowDownLeft className="w-3 h-3 text-green-600" />
                              ) : (
                                <ArrowUpRight className="w-3 h-3 text-red-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-sm text-slate-900">
                                {tx.clean_description || '-'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className={`px-4 py-3 text-right font-medium ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(tx.amount)}
                        </td>
                        <td className="px-4 py-3">
                          {tx.facture ? (
                            <div>
                              <button
                                onClick={() => handleViewPdf(tx)}
                                className="flex items-center gap-2 hover:underline"
                              >
                                <FileText className="w-4 h-4 text-blue-500" />
                                <span className="font-medium text-blue-600">{tx.facture.numero}</span>
                                <Eye className="w-3 h-3 text-slate-400" />
                              </button>
                              <p className="text-sm text-slate-600">{tx.facture.client}</p>
                              <p className="text-xs text-slate-500">
                                {formatDate(tx.facture.date)} • {formatCurrency(tx.facture.total_ttc)}
                              </p>
                            </div>
                          ) : (
                            <span className="text-slate-400">{tx.facture_rapprochee_numero || '-'}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={mode.color}>{mode.label}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            {tx.facture?.has_pdf && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewPdf(tx)}
                                title="Voir le PDF"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUnlink(tx)}
                              disabled={unlinking}
                              title="Annuler le rapprochement"
                              className="text-red-500 hover:text-red-700"
                            >
                              <Unlink className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Page {page} sur {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Modal PDF */}
      <Dialog open={showPdfModal} onOpenChange={setShowPdfModal}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {selectedTx?.facture?.numero} - {selectedTx?.facture?.client}
            </DialogTitle>
          </DialogHeader>
          <div className="h-[70vh]">
            {pdfUrl && (
              <iframe
                src={pdfUrl}
                className="w-full h-full border-0 rounded"
                title="PDF Preview"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
